import { useState } from "react";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useEventCategories, useDeleteEventCategory } from "@/hooks/useEventManagement";
import { EventCategoryItem } from "./EventCategoryItem";

interface EventCategoriesListProps {
  onEdit: (categoryId: string) => void;
}

export function EventCategoriesList({ onEdit }: EventCategoriesListProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  const { data: categories, isLoading, error } = useEventCategories();
  const deleteCategory = useDeleteEventCategory();

  const handleDelete = (categoryId: string) => {
    setCategoryToDelete(categoryId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (categoryToDelete) {
      await deleteCategory.mutateAsync(categoryToDelete);
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  const toggleActive = async (categoryId: string, currentActive: boolean) => {
    // For now, we don't implement toggle active for categories
    // This could be added later if needed
    console.log('Toggle active not implemented for categories yet');
  };

  const handleDragEnd = async (result: DropResult) => {
    // For now, reordering is not implemented for categories
    // This could be added later if needed
    console.log('Drag and drop reordering not implemented for categories yet');
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-destructive">
        <p>Error loading event categories</p>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : "Unknown error"}
        </p>
      </div>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No event categories found</p>
        <p className="text-sm">Create your first event category to get started</p>
      </div>
    );
  }

  return (
    <>
      <div>
        {reordering && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            <span className="text-sm text-blue-800">Updating category order...</span>
          </div>
        )}
        
        <div className="space-y-2">
          {categories.map((category, index) => (
            <EventCategoryItem
              key={category.id}
              category={category}
              index={index}
              onEdit={onEdit}
              onDelete={handleDelete}
              onToggleActive={toggleActive}
              reordering={reordering}
            />
          ))}
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event Category</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Are you sure you want to delete this event category? This action cannot be undone
                and may affect communication templates and event flows that use this category.
              </p>
              <p className="text-amber-600 font-medium">
                ⚠️ Make sure no templates or flows are using this category before deleting.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}